/**
 * TellerWand PWA Install Banner Component
 *
 * Features:
 * - Fixed bottom banner with logo, install button, close button
 * - Slide-in/out animations + pulse effect on install button
 * - Shows once per user (localStorage)
 * - Auto-hide after 10 seconds
 * - Android/Chrome: Native install prompt
 * - iOS Safari: Custom instructions
 * - Analytics tracking
 * - Fully responsive
 */

import { useState, useEffect, useCallback } from "react";
import { X, Download, Share } from "lucide-react";
import { devLog } from "@/utils/devLog";
import { toast } from "@/components/ui/use-toast";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface WindowWithDeferredPrompt extends Window {
  deferredPrompt?: BeforeInstallPromptEvent;
  navigator: Navigator & {
    standalone?: boolean;
  };
}

interface WindowWithMSStream extends Window {
  MSStream?: unknown;
}

/**
 * PWAInstall Banner.
 */
export default function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const trackEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => {
    // Analytics tracking - integrate with your analytics service
    devLog.warn(`[PWA Analytics] ${eventName}:`, properties);

    // Example: Send to analytics service
    // if (window.gtag) {
    //   window.gtag('event', eventName, properties);
    // }

    // Example: Send to custom analytics endpoint
    // fetch('/api/analytics/track', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ event: eventName, properties })
    // });
  }, []);

  const handleDismiss = useCallback(
    (reason: string) => {
      trackEvent("pwa_banner_dismissed", { reason });

      setIsAnimatingOut(true);

      // Wait for animation to complete before hiding
      setTimeout(() => {
        setShowBanner(false);
        localStorage.setItem("tellerwand-pwa-banner-dismissed", "true");
        localStorage.setItem("tellerwand-pwa-banner-last-dismissed", Date.now().toString());
      }, 300);
    },
    [trackEvent]
  );

  useEffect(() => {
    // Debug logging
    devLog.warn("[PWA Banner] Initializing...");

    // Check if already in standalone mode (PWA installed)
    const windowWithDeferred = window as WindowWithDeferredPrompt;
    const isInStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      windowWithDeferred.navigator.standalone === true ||
      document.referrer.includes("android-app://");

    devLog.warn("[PWA Banner] Is Standalone:", isInStandalone);
    setIsStandalone(isInStandalone);

    // Detect iOS
    const windowWithMSStream = window as WindowWithMSStream;
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !windowWithMSStream.MSStream;
    devLog.warn("[PWA Banner] Is iOS:", iOS);
    setIsIOS(iOS);

    // Check if user has already seen the banner
    const hasSeenBanner = localStorage.getItem("tellerwand-pwa-banner-dismissed");
    const lastDismissed = localStorage.getItem("tellerwand-pwa-banner-last-dismissed");

    devLog.warn("[PWA Banner] Has seen banner:", hasSeenBanner);
    devLog.warn("[PWA Banner] Last dismissed:", lastDismissed);

    // Show again after 7 days if previously dismissed
    const shouldShowAgain = lastDismissed
      ? Date.now() - Number.parseInt(lastDismissed, 10) > 7 * 24 * 60 * 60 * 1000
      : true;

    devLog.warn("[PWA Banner] Should show again:", shouldShowAgain);

    // Don't show if already installed or recently dismissed
    if (isInStandalone) {
      devLog.warn("[PWA Banner] Not showing - already installed");
      return;
    }

    if (hasSeenBanner && !shouldShowAgain) {
      devLog.warn("[PWA Banner] Not showing - recently dismissed");
      return;
    }

    // Track banner view
    trackEvent("pwa_banner_viewed", {
      platform: iOS ? "ios" : "android",
      userAgent: navigator.userAgent,
    });

    // Show banner after 2 seconds delay (let page load first)
    const showTimer = setTimeout(() => {
      devLog.warn("[PWA Banner] Showing banner now");
      setShowBanner(true);
    }, 2000);

    // Auto-hide after 10 seconds
    const hideTimer = setTimeout(() => {
      devLog.warn("[PWA Banner] Auto-hiding banner");
      handleDismiss("auto-hide");
    }, 12000); // 2s delay + 10s display

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      devLog.warn("[PWA Banner] ✅ beforeinstallprompt event received!");
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if prompt is already available
    if (windowWithDeferred.deferredPrompt) {
      devLog.warn("[PWA Banner] Using existing deferred prompt");
      setDeferredPrompt(windowWithDeferred.deferredPrompt);
    }

    // Check for install prompt availability (silently)
    // Note: No warning needed - this is normal behavior when:
    // - PWA already installed
    // - Browser doesn't support prompts (iOS, Firefox, etc.)
    // - Service worker still registering
    setTimeout(() => {
      if (!deferredPrompt && !iOS) {
        devLog.warn("[PWA Banner] Install prompt not available (this is normal for some browsers)");
      }
    }, 5000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, [deferredPrompt, handleDismiss, trackEvent]);

  /**
   * Handle Install.
   */
  const handleInstall = async () => {
    devLog.warn("[PWA Banner] Install button clicked");
    devLog.warn("[PWA Banner] Is iOS:", isIOS);
    devLog.warn("[PWA Banner] Has deferred prompt:", !!deferredPrompt);

    if (isIOS) {
      // iOS doesn't support programmatic install, show instructions
      trackEvent("pwa_install_clicked", { platform: "ios", action: "show_instructions" });
      devLog.warn("[PWA Banner] iOS detected - instructions already visible");

      // Show alert with instructions

      toast({
        title: "Install on iOS",
        description: 'Tap Share (□↑), scroll down, tap "Add to Home Screen", then tap "Add".',
      });
      return;
    }

    if (!deferredPrompt) {
      devLog.warn("[PWA Banner] ⚠️ No install prompt available");
      devLog.warn("[PWA Banner] Checking PWA requirements...");

      // Debug: Check if service worker is registered
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          devLog.warn("[PWA Banner] Service Worker registered:", !!reg);
          if (reg) {
            devLog.warn("[PWA Banner] SW state:", reg.active?.state);
          }
        });
      }

      toast({
        title: "Install TellerWand",
        description:
          "Desktop: use the install icon in the address bar. Mobile: use the browser menu → Install app or Add to Home Screen.",
      });

      trackEvent("pwa_install_clicked", { platform: "other", action: "no_prompt_fallback" });
      return;
    }

    trackEvent("pwa_install_clicked", { platform: "android", action: "trigger_prompt" });
    devLog.warn("[PWA Banner] Triggering install prompt...");

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      devLog.warn("[PWA Banner] Prompt shown, waiting for user response...");

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;

      devLog.warn("[PWA Banner] User choice:", outcome);
      trackEvent("pwa_install_outcome", {
        platform: "android",
        outcome,
      });

      if (outcome === "accepted") {
        devLog.warn("[PWA Banner] ✅ User accepted the install prompt");
        setShowBanner(false);
      } else {
        devLog.warn("[PWA Banner] ❌ User dismissed the install prompt");
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
    } catch (error) {
      devLog.error("[PWA Banner] Error during install:", error);
      trackEvent("pwa_install_error", { error: String(error) });
    }
  };

  // Don't render if shouldn't show
  if (!showBanner || isStandalone) {
    return null;
  }

  return (
    <>
      {/* Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 8px rgba(37, 99, 235, 0);
          }
        }

        @keyframes sparkle {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        .pwa-banner-container {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          animation: slideUp 0.3s ease-out;
          pointer-events: auto;
        }

        .pwa-banner-container.animate-out {
          animation: slideDown 0.3s ease-out;
        }

        .pwa-banner {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-top: 2px solid #2563eb;
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          backdrop-filter: blur(10px);
        }

        .pwa-banner-logo {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #2563eb;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
        }

        .pwa-banner-content {
          flex: 1;
          min-width: 0;
        }

        .pwa-banner-title {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin: 0 0 2px 0;
        }

        .pwa-banner-subtitle {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
        }

        .pwa-banner-ios-hint {
          font-size: 11px;
          color: #60a5fa;
          margin: 4px 0 0 0;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pwa-banner-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        }

        .pwa-install-btn {
          background: #2563eb;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
          animation: pulse 2s infinite;
          white-space: nowrap;
        }

        .pwa-install-btn:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
        }

        .pwa-install-btn:active {
          transform: translateY(0);
        }

        .pwa-close-btn {
          background: transparent;
          color: #94a3b8;
          border: none;
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .pwa-close-btn:hover {
          background: rgba(148, 163, 184, 0.1);
          color: #ffffff;
        }

        .sparkle {
          animation: sparkle 1.5s infinite;
        }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .pwa-banner {
            padding: 10px 12px;
            gap: 10px;
          }

          .pwa-banner-logo {
            width: 36px;
            height: 36px;
          }

          .pwa-banner-title {
            font-size: 13px;
          }

          .pwa-banner-subtitle {
            font-size: 11px;
          }

          .pwa-install-btn {
            padding: 8px 16px;
            font-size: 13px;
          }

          .pwa-banner-ios-hint {
            font-size: 10px;
          }
        }

        @media (max-width: 400px) {
          .pwa-banner-subtitle {
            display: none;
          }

          .pwa-install-btn {
            padding: 8px 12px;
            font-size: 12px;
          }
        }
      `}</style>

      {/* Banner */}
      <div className={`pwa-banner-container ${isAnimatingOut ? "animate-out" : ""}`}>
        <div className="pwa-banner">
          {/* Logo */}
          <div className="pwa-banner-logo">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                fill="currentColor"
                fillOpacity="0.3"
                stroke="white"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Content */}
          <div className="pwa-banner-content">
            <h3 className="pwa-banner-title">
              Install TellerWand <span className="sparkle">✨</span>
            </h3>
            <p className="pwa-banner-subtitle">Get faster access and work offline</p>
            {isIOS && (
              <p className="pwa-banner-ios-hint">
                <Share size={12} />
                Tap Share, then "Add to Home Screen"
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="pwa-banner-actions">
            {!isIOS && (
              <button
                className="pwa-install-btn"
                onClick={handleInstall}
                aria-label="Install TellerWand PWA"
              >
                <Download size={16} />
                Install
              </button>
            )}

            <button
              className="pwa-close-btn"
              onClick={() => handleDismiss("user-close")}
              aria-label="Close install banner"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
